// Core
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Components
import ContractCard from "pages/ManageContracts/Single/ContractCard";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openSuccessAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useAddNewContractMutation,
  useUpdateContractMutation,
} from "state/contracts/contractsApiSlice";
import { selectAllMembers, useGetMembersByCommunityIdQuery } from "state/members/membersApiSlice";

// Utils
import { useTranslation } from "react-i18next";
import { voltageLevelTypes } from "utils/constants/index";
import { extractPayload, getDefaultPayload, payloadsAreEqual } from "./utils";

const today = DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd");

function ContractForm({ contract, meterIdentifier, action, contractId, meterId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["contracts"]);

  // --- General state ---
  const [buyType, setBuyType] = useState(contract?.buy_type || "NO_VALUES");
  const [sellType, setSellType] = useState(contract?.sell_type || "NO_VALUES");
  const [validity, setValidity] = useState(contract?.validity || today);
  const [voltageLevel, setVoltageLevel] = useState(contract?.voltage_level || "");
  const [tariffCycle, setTariffCycle] = useState(contract?.tariff_cycle || "");
  const [withHolidays, setWithHolidays] = useState(contract?.with_holidays || false);
  const [contractedPower, setContractedPower] = useState(contract?.contracted_power || 0);
  const [memberId, setMemberId] = useState(contract?.user_id || "");

  // --- Payload state (one per type per side) ---
  const [buyPayload, setBuyPayload] = useState(
    extractPayload(contract?.buy_payload, contract?.buy_type || "NO_VALUES")
  );
  const [sellPayload, setSellPayload] = useState(
    extractPayload(contract?.sell_payload, contract?.sell_type || "NO_VALUES")
  );

  // Keep original snapshots for dirty-check
  const [originalContract] = useState(() => ({
    buy_type: contract?.buy_type || "NO_VALUES",
    sell_type: contract?.sell_type || "NO_VALUES",
    validity: contract?.validity || today,
    voltage_level: contract?.voltage_level || "",
    tariff_cycle: contract?.tariff_cycle || "",
    with_holidays: contract?.with_holidays || false,
    contracted_power: contract?.contracted_power || 0,
    user_id: contract?.user_id || "",
    buyPayload: extractPayload(contract?.buy_payload, contract?.buy_type || "NO_VALUES"),
    sellPayload: extractPayload(contract?.sell_payload, contract?.sell_type || "NO_VALUES"),
  }));

  // --- Handlers: General (plain values from the AtomsV2 fields) ---
  const onChangeBuyType = (value) => {
    setBuyType(value);
    setBuyPayload(getDefaultPayload(value));
  };
  const onChangeSellType = (value) => {
    setSellType(value);
    setSellPayload(getDefaultPayload(value));
  };

  // --- Handlers: Payloads ---
  const onChangeBuyPayload = (field, value) => {
    setBuyPayload((prev) => ({ ...prev, [field]: parseFloat(value) }));
  };
  const onChangeSellPayload = (field, value) => {
    setSellPayload((prev) => ({ ...prev, [field]: parseFloat(value) }));
  };

  // --- Dirty check ---
  const isOriginal = () => {
    if (action === "new") return false;
    if (!originalContract) return false;
    return (
      originalContract.buy_type === buyType &&
      originalContract.sell_type === sellType &&
      originalContract.validity === validity &&
      originalContract.voltage_level === voltageLevel &&
      originalContract.tariff_cycle === tariffCycle &&
      originalContract.with_holidays === withHolidays &&
      originalContract.contracted_power === contractedPower &&
      originalContract.user_id === memberId &&
      payloadsAreEqual(originalContract.buyPayload, buyPayload) &&
      payloadsAreEqual(originalContract.sellPayload, sellPayload)
    );
  };

  // --- Contracted power validation ---
  const saveContractedPowerCheck = () => {
    if (
      voltageLevel === "BTN simples" ||
      voltageLevel === "BTN bi-horária" ||
      voltageLevel === "BTN< tri-horária"
    ) {
      return [1.15, 2.3, 3.45, 4.6, 5.75, 6.9, 10.35, 13.8, 17.25, 20.7].includes(contractedPower);
    }
    if (voltageLevel === "BTN>") {
      return [27.6, 34.5, 41.4].includes(contractedPower);
    }
    if (voltageLevel === "BTE") {
      return contractedPower > 41.4;
    }
    return true;
  };

  const canSave =
    [buyType, sellType, voltageLevel, tariffCycle].every((val) => val.length > 0) &&
    !isOriginal() &&
    memberId.length > 0 &&
    saveContractedPowerCheck();

  // --- Members ---
  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ members: data && selectAllMembers(data) }),
    }
  );
  const memberOptions = members?.map((member) => ({
    value: member.id,
    label: member.name,
  }));

  // --- Save ---
  const [addNewContract] = useAddNewContractMutation();
  const [updateContract] = useUpdateContractMutation();

  const buildPayload = (type, payload, buySellType) => {
    if (type === "NO_VALUES") return null;
    return { ...payload, buy_or_sell_type: buySellType };
  };

  const onSaveContract = async () => {
    if (!canSave) return;

    const newContract = {
      buy_type: buyType,
      sell_type: sellType,
      voltage_level: voltageLevel,
      tariff_cycle: tariffCycle,
      with_holidays: withHolidays,
      contracted_power: contractedPower,
      validity,
      user_id: memberId,
      meter_id: meterId,
      buy_payload: buildPayload(buyType, buyPayload, "CONTRACT_BUY_VALUES"),
      sell_payload: buildPayload(sellType, sellPayload, "CONTRACT_SELL_VALUES"),
    };

    try {
      if (action === "new") {
        await addNewContract({
          contract: newContract,
          communityId: community.id,
          meterId,
        }).unwrap();
        navigate(-1);
      } else {
        await updateContract({
          contract: newContract,
          contractId,
          meterId,
          communityId: community.id,
        }).unwrap();
        dispatch(
          openSuccessAlert({
            title: t("notifications.updated_title"),
            description: t("notifications.updated_ok"),
          })
        );
      }
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  if ((action === "view" || action === "edit") && !contract) return null;

  return (
    <ContractCard
      action={action}
      contract={contract}
      meterIdentifier={meterIdentifier}
      meterId={meterId}
      memberOptions={memberOptions}
      memberId={memberId}
      onChangeMember={setMemberId}
      validity={validity}
      onChangeValidity={setValidity}
      buyType={buyType}
      sellType={sellType}
      onChangeBuyType={onChangeBuyType}
      onChangeSellType={onChangeSellType}
      voltageLevelOptions={voltageLevelTypes}
      voltageLevel={voltageLevel}
      onChangeVoltageLevel={setVoltageLevel}
      tariffCycle={tariffCycle}
      onChangeTariffCycle={setTariffCycle}
      withHolidays={withHolidays}
      onChangeWithHolidays={setWithHolidays}
      contractedPower={contractedPower}
      onChangeContractedPower={setContractedPower}
      buyPayload={buyPayload}
      sellPayload={sellPayload}
      onChangeBuyPayload={onChangeBuyPayload}
      onChangeSellPayload={onChangeSellPayload}
      canConfirm={canSave}
      onConfirm={onSaveContract}
    />
  );
}

ContractForm.defaultProps = {
  contractId: "",
  meterIdentifier: "",
  contract: null,
};

ContractForm.propTypes = {
  contractId: PropTypes.string,
  action: PropTypes.string.isRequired,
  meterIdentifier: PropTypes.string,
  meterId: PropTypes.string.isRequired,
  contract: PropTypes.shape({
    buy_type: PropTypes.string.isRequired,
    sell_type: PropTypes.string.isRequired,
    meter_id: PropTypes.string.isRequired,
    validity: PropTypes.string.isRequired,
    voltage_level: PropTypes.string.isRequired,
    contracted_power: PropTypes.number.isRequired,
    with_holidays: PropTypes.bool.isRequired,
    tariff_cycle: PropTypes.string.isRequired,
    user_id: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    buy_payload: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    sell_payload: PropTypes.object,
  }),
};

export default ContractForm;
